import * as React from "react";
import { CalendarCheck, CalendarX2, Clock } from "lucide-react";

import { apiErrorMessage, cancelBooking, getBookingManage } from "@/api/client";
import { PublicBookingManageView } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { PublicFooter } from "./public/PublicChrome";
import { PublicLoadingLoop, useLoadingWindow } from "./public/PublicLoading";

type Load =
  | { status: "loading" }
  | { status: "error"; notFound: boolean }
  | { status: "ready"; view: PublicBookingManageView };

function whenLabel(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const day = start.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const from = start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const to = new Date(endIso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${from}–${to}`;
}

/**
 * The invitee's self-service page at {@code /book/manage/{token}} — no account,
 * resolved from the unguessable token in their confirmation email. Shows the
 * booking and, when the host allowed it and the meeting hasn't started, a
 * Cancel button. (Reschedule is a host opt-in wired in a follow-up.)
 */
export function PublicBookingManagePage({ token }: { token: string }) {
  const [load, setLoad] = React.useState<Load>({ status: "loading" });
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    getBookingManage(token)
      .then((view) => {
        if (!cancelled) setLoad({ status: "ready", view });
      })
      .catch((e) => {
        if (!cancelled) {
          const notFound = apiErrorMessage(e).includes("404");
          setLoad({ status: "error", notFound });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function doCancel() {
    setBusy(true);
    setError(null);
    try {
      const view = await cancelBooking(token);
      setLoad({ status: "ready", view });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // Held on screen for one full revolution once it appears, and not painted at all
  // if the fetch beats 120ms. The ready and error branches gate on it too: during
  // the hold the fetch has finished, so otherwise both would render at once.
  const showLoading = useLoadingWindow(load.status === "loading");

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-blue/10 via-background to-brand-teal/10 px-4 py-12">
      <div className="mx-auto max-w-md">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_60px_-32px_rgba(15,23,42,0.28)]">
          {showLoading && (
            <PublicLoadingLoop label="Loading your booking" />
          )}

          {!showLoading && load.status === "error" && (
            <div className="py-6 text-center">
              <CalendarX2 className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm font-semibold text-foreground">
                {load.notFound ? "We couldn't find that booking" : "Something went wrong"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                The link may be old or already used. Check your confirmation email for the latest one.
              </p>
            </div>
          )}

          {!showLoading && load.status === "ready" && (
            <ManageBody
              view={load.view}
              busy={busy}
              error={error}
              onCancel={doCancel}
            />
          )}
        </div>

        <PublicFooter className="mt-6" />
      </div>
    </div>
  );
}

function ManageBody({
  view,
  busy,
  error,
  onCancel,
}: {
  view: PublicBookingManageView;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
}) {
  const cancelled = view.status === "cancelled";
  return (
    <>
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "grid h-10 w-10 shrink-0 place-items-center rounded-full",
            cancelled ? "bg-muted text-muted-foreground" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
          )}
        >
          {cancelled ? <CalendarX2 className="h-5 w-5" /> : <CalendarCheck className="h-5 w-5" />}
        </span>
        <div className="min-w-0">
          <h1 className={cn("font-display text-lg font-bold leading-tight", cancelled && "line-through")}>
            {view.title}
          </h1>
          <p className="text-xs text-muted-foreground">with {view.host_name}</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 rounded-2xl bg-muted/40 px-4 py-3 text-sm">
        <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className={cn("font-medium", cancelled && "text-muted-foreground line-through")}>
          {whenLabel(view.start, view.end)}
        </span>
      </div>

      {cancelled ? (
        <div className="mt-5 space-y-3 text-center">
          <p className="rounded-2xl bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            This booking has been cancelled. The time has been freed up.
          </p>
          <a
            href={view.booking_url}
            className="inline-block rounded-full bg-primary px-5 py-2 font-display text-sm font-semibold text-primary-foreground shadow-sm transition hover:brightness-110"
          >
            Book a different time →
          </a>
        </div>
      ) : view.cancellable ? (
        <div className="mt-5 space-y-3">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={onCancel}
            className="w-full border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-950/30"
          >
            {busy ? "Cancelling…" : "Cancel this booking"}
          </Button>
          <p className="text-center text-[11px] text-muted-foreground">
            The host will be notified and the slot will reopen for someone else.
          </p>
        </div>
      ) : (
        <p className="mt-5 rounded-2xl bg-muted/40 px-4 py-3 text-center text-xs text-muted-foreground">
          This booking can't be changed online. To make a change, reply to your confirmation email.
        </p>
      )}

      {error && (
        <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-center text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </p>
      )}
    </>
  );
}
